import { useEffect, useState } from 'react'
import { type PortalChildSummary, type PortalCaregiverSummary } from 'core/types'
import { sendGet } from 'hooks/use-fetch'
import { LargeCard } from 'Components/Cards/Large'
import { Box, Button, Container, Typography, Stack, Grid } from '@mui/material'
import { childAge, childName } from 'Pages/CaregiverPortal/PortalHelpers'
import { BackArrow } from 'Assets/SVGs/back-arrow'
import { generatePath, useNavigate } from 'react-router'
import { CAREGIVER_PORTAL_DASHBOARD, CAREGIVER_PORTAL_MEMBERS_CAREGIVER_DETAILS, CAREGIVER_PORTAL_MEMBERS_CHILD_DETAILS, CAREGIVER_PORTAL_MEMBERS_EDIT_CAREGIVER, CAREGIVER_PORTAL_MEMBERS_EDIT_CHILD } from 'Pages/Routes'
import { type DuplicateContactInfo } from './DuplicateFound'

export function CaregiverMembers (): JSX.Element {
  const nav = useNavigate()
  const [children, setChildren] = useState<PortalChildSummary[]>([])
  const [caregivers, setCaregivers] = useState<PortalCaregiverSummary[]>([])
  const [pendingDupChildren, setPendingDupChildren] = useState<PortalChildSummary[]>([])
  const [duplicateContact, setDuplicateContact] = useState<DuplicateContactInfo | null>()

  useEffect(() => {
    const getData = async (): Promise<void> => {
      const { response, success } = await sendGet('/CaregiverPortal/GetRelatedMembers', {})
      if (success) {
        setChildren(response.children)
        setCaregivers(response.caregivers)
      }

      const { response: rsp2, success: suc2 } = await sendGet('/CaregiverPortal/GetPendingDuplicates', {})
      if (suc2) {
        setPendingDupChildren(rsp2)

        if (rsp2.length > 0) {
          const { response: isdRsp } = await sendGet('/CaregiverPortal/GetISDSettings?id=', {})
          const isdId: string = isdRsp?.isdid
          const { response: isdDetails } = await sendGet(`/ISDDetails/GetDetails?id=${isdId}`, {})
          const { response: mieccContact } = await sendGet('/SiteConfiguration/GetDuplicateHandlingContact', {})
          if (isdDetails?.duplicateContactName || isdDetails?.duplicateContactEmail) {
            setDuplicateContact({
              name: isdDetails.duplicateContactName,
              email: isdDetails.duplicateContactEmail,
              phone: isdDetails.duplicateContactPhone
            })
          } else if (mieccContact.name || mieccContact.email) {
            setDuplicateContact({
              name: mieccContact.name,
              email: mieccContact.email
            })
          } else {
            setDuplicateContact({ name: isdDetails.name })
          }
        }
      }
    }

    void getData()
  }, [])

  const addChild = (): void => {
    const route = generatePath(CAREGIVER_PORTAL_MEMBERS_EDIT_CHILD, { childId: 'new' })
    nav(route)
  }

  const addCaregiver = (): void => {
    const route = generatePath(CAREGIVER_PORTAL_MEMBERS_EDIT_CAREGIVER, { caregiverId: 'new' })
    nav(route)
  }

  const viewChild = (childId: string): void => {
    const route = generatePath(CAREGIVER_PORTAL_MEMBERS_CHILD_DETAILS, { childId })
    nav(route)
  }

  const viewCaregiver = (caregiverId: string): void => {
    const route = generatePath(CAREGIVER_PORTAL_MEMBERS_CAREGIVER_DETAILS, { caregiverId })
    nav(route)
  }

  return <Container sx={{ mb: '30px' }}>
    <Box sx={{ mt: '30px', mb: '30px', display: 'flex' }}>
      <Box onClick={() => { nav(CAREGIVER_PORTAL_DASHBOARD) }} sx={{ cursor: 'pointer' }}>
        <BackArrow />
      </Box>
      <Typography variant='h5' sx={{ fontWeight: 600, ml: '5px' }} component='div'>
        Members
      </Typography>
    </Box>

    <Grid container spacing={2}>
      {children.map((child) => {
        return <Grid item xs={12} sm={12} md={6}>
          <LargeCard
            key={child.id}
            fullWidth={true}
            className='member-card'
            onClick={() => { viewChild(child.id.toString()) }}
            content={<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '1.1em' }}>
              <Box>
                <Box sx={{ fontWeight: '600' }}>{childName(child)}</Box>
                <Box>{childAge(child)}</Box>
              </Box>
            </Box>}
          />
        </Grid>
      })}

      {pendingDupChildren.map((child) => {
        return <Grid item xs={12} sm={12} md={6}>
          <LargeCard
            key={child.id}
            fullWidth={true}
            className='member-card'
            content={<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ pr: 2 }}>
                <Box sx={{ fontWeight: '600' }}>{childName(child)}</Box>
                <Box>{childAge(child)}</Box>
              </Box>
              <Box>
                <Stack direction="row">This child is pending duplicate resolution.</Stack>
                {duplicateContact !== undefined && <Stack direction="row">For information on the status of this you can reach out to {duplicateContact?.name} at {duplicateContact?.email}.</Stack>}
              </Box>
            </Box>}
          />
        </Grid>
      })}
    </Grid>

    <Button
      variant='contained'
      className='footer-button'
      sx={{ mt: '20px' }}
      name='addChildButton'
      onClick={addChild}
    >
      Add Child
    </Button>

    <Grid container spacing={2}>
      {caregivers.map((caregiver) => {
        return <Grid item xs={12} sm={12} md={6}>
          <LargeCard
            key={caregiver.id}
            fullWidth={true}
            className='member-card'
            onClick={() => { viewCaregiver(caregiver.id.toString()) }}
            content={<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '1.1em' }}>
              <Box>
                <Box sx={{ fontWeight: '600' }}>{`${caregiver.firstName} ${caregiver.lastName}`}</Box>
                <Box>{caregiver.phoneNumber}</Box>
                <Box>{caregiver.email}</Box>
              </Box>
            </Box>}
          />
        </Grid>
      })}
    </Grid>

    <Button
      variant='contained'
      className='footer-button'
      sx={{ mt: '20px' }}
      name='addCaregiverButton'
      onClick={addCaregiver}
    >
      Add Caregiver
    </Button>
  </Container>
}
